/**
 * Capability semantics — a faithful client mirror of the server resolver
 * (server/src/platform/rbac/capabilityResolver.js) for ANY membership delta the client knows
 * locally. The SERVER is always authoritative: the store seeds `can()` from a live server fetch
 * (the JWT carries no capabilities — F-02). This module exists so the client can (a) reason about
 * the effective set with the exact same rules and (b) recompute optimistically when a grant/deny
 * signal arrives, before the server round-trip confirms.
 *
 *   effective = ( ⋃ caps(preset) ) ∪ grants − denies      (deny-wins, super-grant)
 *   owner (est_proprietaire) = FULL catalog − (denies ∉ owner-trio)
 */

/** The owner-scoped trio — owner-only, never removed below the owner (server §3.4). */
export const OWNER_SCOPED = Object.freeze([
  "iam.utilisateur.manage",
  "catalogue.parametre.price",
  "audit.journal.read_all",
] as const);

const OWNER_SCOPED_SET: ReadonlySet<string> = new Set(OWNER_SCOPED);

/** A membership snapshot — the same shape the server resolver consumes. */
export interface MembershipSnapshot {
  presets: string[];
  est_proprietaire: boolean;
  grants: string[];
  denies: string[];
}

/**
 * The effective set. When `fullCatalog` is provided (owner path), pass the live catalog the
 * server exposes via its introspection surface; otherwise the owner branch returns whatever
 * catalog set is supplied. For non-owner memberships, `presetCaps` maps preset id -> capability[]
 * (mirror of rbac.config.js presets, fetched live so the client never hardcodes the bundles).
 */
export function computeEffectiveSet(
  snapshot: MembershipSnapshot,
  presetCaps: Readonly<Record<string, ReadonlyArray<string>>>,
  fullCatalog: ReadonlyArray<string> = [],
): Set<string> {
  const denySet = new Set(snapshot.denies);

  if (snapshot.est_proprietaire) {
    const eff = new Set(fullCatalog);
    for (const d of denySet) {
      if (!OWNER_SCOPED_SET.has(d)) eff.delete(d); // owner trio never denied below the owner
    }
    return eff;
  }

  const eff = new Set<string>();
  for (const p of snapshot.presets) {
    for (const c of presetCaps[p] ?? []) eff.add(c);
  }
  for (const g of snapshot.grants) eff.add(g); // grant = super-grant
  for (const d of denySet) eff.delete(d); // deny wins (final)
  return eff;
}

/** Synchronous capability check against an effective Set (the can() core). */
export function hasCapability(effective: ReadonlySet<string>, capability: string): boolean {
  return effective.has(capability);
}
